// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import OEOrderTypeSelectorTemplate from '@front/templates/OEOrderTypeSelectorTemplate.html';
import { ContainerControl } from '@front/controls/elements/ContainerControl';
import { Resources } from '@shared/localizations/Resources';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { StopLimitOrderType } from '@shared/commons/cache/OrderParams/order-type/StopLimitOrderType';
import { StopOrderType } from '@shared/commons/cache/OrderParams/order-type/StopOrderType';
import { OrderExecutorUtils } from '@shared/commons/Trading/OrderExecutorUtils';
import { WDSettings } from '../../settings/WDGeneralSettings';
import { ResourcesManager } from '@shared/commons/properties/ResourcesManager';
import { DataCache } from '@shared/commons/DataCache';
import { NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
export class OEOrderTypeSelector extends ContainerControl {
    constructor() {
        super();
        this.useStopLimitInsteadStop = false;
    }
    getType() {
        return 'OEOrderTypeSelector';
    }
    oninit() {
        super.oninit();
        this.observe('selectedItem', this.updateOrderType);
        this.observe('instrument account forbiddenOrderTypes', this.repopulate);
        ResourcesManager.onLocaleChange.Subscribe(this.localize, this);
        WDSettings.settingsChanged.Subscribe(this.updateUseStopLimitInsteadStopSetting, this);
        this.localize();
    }
    oncomplete() {
        super.oncomplete();
        DataCache.OnUpdateInstrument.Subscribe(this.onUpdateInstrument, this);
    }
    dispose() {
        ResourcesManager.onLocaleChange.UnSubscribe(this.localize, this);
        WDSettings.settingsChanged.UnSubscribe(this.updateUseStopLimitInsteadStopSetting, this);
        DataCache.OnUpdateInstrument.UnSubscribe(this.onUpdateInstrument, this);
        super.dispose();
    }
    localize() {
        void this.set('label', Resources.getResource('panel.newOrderEntry.typePanel'));
        void this.set('infoBtnTooltip', Resources.getResource('panel.newOrderEntry.stopInfoBtn.tt'));
    }
    updateOrderType(selectedItem) {
        if (isNullOrUndefined(selectedItem)) {
            void this.set('orderType', null);
            return;
        }
        const orderType = selectedItem.value;
        void this.set('orderType', orderType);
        this.updateUseStopLimitInsteadStopSetting();
    }
    repopulate() {
        const instrument = this.get('instrument');
        const account = this.get('account');
        if (isNullOrUndefined(instrument) || isNullOrUndefined(account)) {
            return;
        }
        const forbiddenOrderTypes = this.get('forbiddenOrderTypes');
        const orderTypeDict = OrderExecutorUtils.getAllowedOrderTypeDict(account, instrument, forbiddenOrderTypes, undefined, false);
        const orderTypeSortedArr = OEOrderTypeSelector.createSortedOrderTypeArray(orderTypeDict);
        const lastSelectedItem = this.get('selectedItem');
        let selectedOrderType = WDSettings.orderType;
        if (!isNullOrUndefined(lastSelectedItem) &&
            orderTypeSortedArr.some((orderType) => orderType.id() === lastSelectedItem.value.id())) {
            selectedOrderType = lastSelectedItem.value.id();
        }
        // TODO. Common method?
        const items = orderTypeSortedArr.map(function (orderType) {
            return {
                type: NewContextMenuItemType.Default,
                value: orderType,
                text: orderType.localizationKey()
            };
        });
        const selectedItem = items.find((item) => item.value.id() === selectedOrderType);
        void this.set({
            items,
            selectedItem
        });
    }
    updateUseStopLimitInsteadStopSetting() {
        // #91413 вызовы методов апдейта
        const selectedItem = this.get('selectedItem');
        const orderType = !isNullOrUndefined(selectedItem) ? selectedItem.value : null;
        if (isNullOrUndefined(orderType))
            return;
        this.useStopLimitInsteadStop = WDSettings.isUseStopLimitInsteadStop();
        const stopOrderTypeSelected = orderType.id() === OrderType.Stop;
        const needUseStopLimitInsteadStop = this.useStopLimitInsteadStop && stopOrderTypeSelected;
        this.updateInfoBtnVisibility(needUseStopLimitInsteadStop); // обновление видимости инфо кнопки
        this.updateOrderTypeIfUseStopLimitInsteadStop(stopOrderTypeSelected, needUseStopLimitInsteadStop); // наперсточник
    }
    updateInfoBtnVisibility(visible) {
        // изменение видимости инфо иконки #91413
        void this.set('visibleInfoBtn', visible);
    }
    updateOrderTypeIfUseStopLimitInsteadStop(stopOrderTypeSelected, needUseStopLimitInsteadStop) {
        // #91413
        if (needUseStopLimitInsteadStop) {
            void this.set('orderType', new StopLimitOrderType());
        } // подмена orderType в случае включения/выключения настройки useStopLimitInsteadStop или смены orderType при вкл.настройке
        else if (stopOrderTypeSelected) {
            void this.set('orderType', new StopOrderType());
        } // снятие настройки UseStopLimitInsteadStop при выбранном Stop ордере -> он был подменен на StopLimit нужно подменить обратно
    }
    // TODO. Sorting rule.
    static createSortedOrderTypeArray(orderTypeDict) {
        const arr = [];
        if (isNullOrUndefined(orderTypeDict))
            return arr;
        const sortArr = [
            OrderType.Market,
            OrderType.Limit,
            OrderType.Stop,
            OrderType.StopLimit,
            OrderType.TrailingStop,
            OrderType.Care,
            OrderType.OCO
        ];
        for (const oT of sortArr) {
            if (!isNullOrUndefined(orderTypeDict[oT])) {
                const orderType = orderTypeDict[oT];
                arr.push(orderType);
            }
        }
        return arr;
    }
    onUpdateInstrument(updatedInstrument) {
        if (this.get('instrument') !== updatedInstrument)
            return;
        this.repopulate();
    }
}
ContainerControl.extendWith(OEOrderTypeSelector, {
    template: OEOrderTypeSelectorTemplate,
    data: function () {
        return {
            label: '',
            instrument: null,
            account: null,
            items: null,
            selectedItem: null,
            orderType: null,
            forbiddenOrderTypes: [],
            showLabel: true,
            visibleInfoBtn: false,
            infoBtnTooltip: '',
            width: 198
        };
    }
});
